import {
  AccountTree,
  Add,
  TrendingUp
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Snackbar,
  Typography
} from '@mui/material';
import { useCallback, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const PipelinePage = () => {
  const { user, loading: authLoading } = useAuth();
  const [pipeline, setPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [stageDeals, setStageDeals] = useState({});
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dealDialog, setDealDialog] = useState({ open: false, deal: null, stageId: null });
  const [dealFormData, setDealFormData] = useState({
    title: '',
    client_id: '',
    product_id: '',
    quantity: 1,
    amount: 0,
    status: 'new'
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount || 0);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Заголовок */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            p: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountTree sx={{ fontSize: 40, color: '#3B82F6' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Воронка продаж
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Новая воронка
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
            >
              Новая сделка
            </Button>
          </Box>
        </Box>

        {/* Информационное сообщение */}
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <TrendingUp sx={{ fontSize: 60, color: '#3B82F6', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Воронка продаж находится в разработке
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Этот модуль позволит вам визуально управлять процессом продаж,
            перемещать сделки между этапами и отслеживать конверсию.
          </Typography>

          {/* Демо этапы */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Пример этапов воронки:
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {[
                { name: 'Лид', color: '#EF4444', probability: 10 },
                { name: 'Квалифицирован', color: '#F59E0B', probability: 25 },
                { name: 'Предложение', color: '#3B82F6', probability: 50 },
                { name: 'Переговоры', color: '#8B5CF6', probability: 75 },
                { name: 'Закрыта', color: '#10B981', probability: 100 }
              ].map((stage, index) => (
                <Grid item key={index}>
                  <Card
                    sx={{
                      minWidth: 120,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: stage.color,
                          margin: '0 auto 8px'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {stage.name}
                      </Typography>
                      <Chip
                        label={`${stage.probability}%`}
                        size="small"
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          fontSize: '10px'
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Snackbar для уведомлений */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default PipelinePage;
